import {useEffect} from "react";
import {getTurnstileConfigQuery} from "../api/graphql/turnstile";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
// cloudflare tokens live five minutes; a pre-solved one is only handed out well inside that
const TOKEN_MAX_AGE_MS = 4 * 60 * 1000;

let configLoading = null;
let scriptLoading = null;
let host = null;
// one widget at a time: the host has a single container
let queue = Promise.resolve();
let prefetched = null;
let prefetching = null;
// why the last solve produced no token, and the action whose check could not run at all
let failure = null;
let unavailableAction = null;

const REQUIRED_KEY = "admin.err.turnstile_required";
const UNAVAILABLE_KEY = "turnstile.unavailable";

// the host is mounted once in App and lends its container and dialog to every check
export function registerTurnstileHost(nextHost) {
    host = nextHost;

    return () => {
        if (host === nextHost) {
            host = null;
        }
    };
}

// read fresh every time, so an action an admin switches on or off applies to the very next submit;
// reads that overlap share one request
function loadConfig() {
    if (!configLoading) {
        configLoading = getTurnstileConfigQuery()
            .then((result) => (result?.__typename === "TurnstileConfig" ? result : {isEnabled: false}))
            .catch(() => ({isEnabled: false}))
            .finally(() => {
                configLoading = null;
            });
    }

    return configLoading;
}

async function isRequired(action) {
    const current = await loadConfig();
    return !!(current?.isEnabled && current?.siteKey && (current?.enabledActions || []).includes(action));
}

function loadScript() {
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }

    if (!scriptLoading) {
        scriptLoading = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = SCRIPT_SRC;
            script.async = true;
            script.onload = () => resolve(window.turnstile);
            script.onerror = () => {
                scriptLoading = null;
                script.remove();
                reject(new Error("turnstile script failed to load"));
            };
            document.head.appendChild(script);
        });
    }

    return scriptLoading;
}

// interactive: a check Cloudflare wants clicked is shown to the client; otherwise it is dropped,
// so a background pre-solve never pops a dialog over a page the client is only reading
function solve(action, interactive) {
    const run = async () => {
        failure = null;
        const current = await loadConfig();
        const turnstile = await loadScript().catch(() => null);

        if (!host?.container || !turnstile) {
            failure = "unavailable";
            return null;
        }

        return new Promise((resolve) => {
            let widgetId = null;
            let done = false;

            const finish = (token, reason) => {
                if (done) return;
                done = true;
                failure = token ? null : reason;
                host?.setInteractive(false);
                // removed after cloudflare's own callback has returned: removing the widget from
                // inside it pulls the iframe out from under their script, which then throws
                setTimeout(() => {
                    try {
                        if (widgetId !== null) turnstile.remove(widgetId);
                    } catch (error) {
                        // already removed
                    }
                }, 0);
                resolve(token || null);
            };

            widgetId = turnstile.render(host.container, {
                sitekey: current.siteKey,
                action,
                appearance: "interaction-only",
                callback: (token) => finish(token),
                // returning true stops cloudflare retrying on its own; the next submit tries again
                "error-callback": () => {
                    finish(null, "unavailable");
                    return true;
                },
                "expired-callback": () => finish(null, "unavailable"),
                "timeout-callback": () => finish(null, "unavailable"),
                "before-interactive-callback": () => {
                    if (interactive) {
                        host?.setInteractive(true, () => finish(null, "cancelled"));
                    } else {
                        finish(null, "deferred");
                    }
                },
            });
        });
    };

    const result = queue.then(run, run).catch(() => null);
    queue = result;
    return result;
}

function takePrefetched(action) {
    const fresh = prefetched?.action === action && Date.now() - prefetched.at < TOKEN_MAX_AGE_MS;
    const token = fresh ? prefetched.token : null;
    prefetched = null;
    return token;
}

// solves ahead of the submit, so a protected form usually sends without waiting
export async function prefetchTurnstileToken(action) {
    try {
        if (!(await isRequired(action))) return;
        if (prefetched?.action === action && Date.now() - prefetched.at < TOKEN_MAX_AGE_MS) return;
        if (prefetching?.action === action) return;

        const entry = {action, promise: solve(action, false), claimed: false};
        prefetching = entry;
        const token = await entry.promise;

        if (prefetching === entry) {
            prefetching = null;
        }

        // a submit that waited on this solve already used the token, and a token works only once
        if (token && !entry.claimed) {
            prefetched = {action, token, at: Date.now()};
        }
    } catch (error) {
        prefetching = null;
    }
}

// for the page a protected form sits on, so the token is ready by the time the client submits
export function useTurnstilePrefetch(action) {
    useEffect(() => {
        prefetchTurnstileToken(action);
    }, [action]);
}

// null when the action is not protected, or when no token could be had; the backend then
// decides, so a broken widget shows the backend's message rather than blocking silently
export async function getTurnstileToken(action) {
    try {
        if (!(await isRequired(action))) return null;

        let token = null;

        if (prefetching?.action === action) {
            const entry = prefetching;
            entry.claimed = true;
            token = await entry.promise;
        }

        token = token || takePrefetched(action);
        const fromPrefetch = !!token;

        if (!token) {
            token = await solve(action, true);
        }

        unavailableAction = !token && failure === "unavailable" ? action : null;

        // a token is single-use, and a wrong password is usually followed by another try
        if (fromPrefetch) {
            prefetchTurnstileToken(action);
        }

        return token;
    } catch (error) {
        unavailableAction = action;
        return null;
    }
}

// spread into a request's input: the field is left out when there is no token, so these requests
// stay valid against a backend that predates turnstile and would reject an unknown field
export async function turnstileField(action) {
    const token = await getTurnstileToken(action);
    return token ? {turnstileToken: token} : {};
}

// the backend only knows no token arrived; when the widget could not run at all, asking the client
// to complete a check they were never shown would mislead, so the refusal is reworded here
export function explainTurnstileRefusal(action, result) {
    if (unavailableAction !== action || !result) {
        return result;
    }

    unavailableAction = null;

    return Object.fromEntries(
        Object.entries(result).map(([name, payload]) => [
            name,
            payload?.errKey === REQUIRED_KEY ? {...payload, errKey: UNAVAILABLE_KEY} : payload,
        ]),
    );
}
